export type Role = "agent" | "acm_member" | "acm_admin";
export type Actor = { userId: string; organizationId: string; role: Role; displayName?: string; email?: string };
export type AcmStatus = "draft" | "in_progress" | "pending_review" | "in_review" | "changes_requested" | "approved" | "rejected" | "completed" | "cancelled";
export type Acm = { id: string; ownerId: string; title: string; status: AcmStatus; createdAt: string; updatedAt: string; submissionNumber: number; analysisVersionNumber: number; everSubmittedForReview: boolean; activeReviewerId: string | null };
export type AcmFile = { id: string; acmId: string; purpose: "target_photo" | "publication_image" | "attachment"; originalName: string; mediaType: "image/jpeg" | "image/png" | "image/webp" | "application/pdf"; sizeBytes: number; uploadedAt: string };
export type ManagedUser = {
  id: string;
  organizationId: string;
  displayName: string;
  email: string;
  role: Role;
  active: boolean;
  identityIssuer: string;
  identitySubject: string;
  updatedAt: string;
};

type ApiErrorBody = { error?: string; message?: string };
type RequestOptions = Readonly<{ unauthenticated?: "expire-session" | "ignore" }>;
const apiBaseUrl = (import.meta as ImportMeta & { env?: Record<string, string | undefined> }).env?.VITE_API_BASE_URL ?? "";

export class ApiRequestError extends Error {
  constructor(readonly status: number, message: string) {
    super(message);
    this.name = "ApiRequestError";
  }
}

export function apiFailureMessage(status: number, body: ApiErrorBody | undefined, fallback: string): string {
  if (status === 401) return "Tu sesión venció. Volvé a ingresar.";
  if (status === 404 && /^Route POST:\/v1\/acms\/[^/]+\/actions\/(trash|restore) not found$/i.test(body?.message ?? "")) {
    return "La API local está ejecutando una versión anterior. Reiniciala para aplicar las acciones de papelera.";
  }
  if (body?.message) return body.message;
  if (status === 403 && body?.error === "invalid_origin") {
    return "Esta ventana usa un origen local no autorizado. Abrí la Plataforma desde el mismo puerto 5173 y reiniciá la API si cambiaste de red.";
  }
  if (status === 429) return "Se realizaron demasiados intentos. Esperá unos minutos y volvé a probar.";
  if (status >= 500) return "El servidor encontró un problema. Volvé a intentar; si continúa, revisá la terminal de la API.";
  return fallback;
}

export function networkFailureMessage(_cause: unknown): string {
  return "No se pudo conectar con el servidor local. Verificá que la API esté iniciada y volvé a intentar.";
}

export class ApiClient {
  constructor(
    private readonly token?: () => Promise<string>,
    private readonly onUnauthenticated?: () => void,
  ) {}

  async request<T>(path: string, init: RequestInit = {}, options: RequestOptions = {}): Promise<T> {
    let response: Response;
    try {
      response = await fetch(`${apiBaseUrl}${path}`, {
        ...init,
        credentials: "include",
        headers: {
          ...(init.body === undefined ? {} : { "Content-Type": "application/json" }),
          ...(this.token ? { Authorization: `Bearer ${await this.token()}` } : {}),
          ...init.headers,
        },
      });
    } catch (cause) { throw new Error(networkFailureMessage(cause)); }
    if (!response.ok) {
      const body = await response.json().catch(() => undefined) as ApiErrorBody | undefined;
      throw this.failure(response.status, body, "No se pudo completar la operación.", options);
    }
    return response.json() as Promise<T>;
  }

  async download(path: string): Promise<{ blob: Blob; filename: string }> {
    let response: Response;
    try {
      response = await fetch(`${apiBaseUrl}${path}`, {
        credentials: "include",
        headers: this.token ? { Authorization: `Bearer ${await this.token()}` } : {},
      });
    } catch (cause) { throw new Error(networkFailureMessage(cause)); }
    if (!response.ok) {
      const body = await response.json().catch(() => undefined) as ApiErrorBody | undefined;
      throw this.failure(response.status, body, "No se pudo preparar la exportación. Revisá los filtros e intentá nuevamente.");
    }
    const disposition = response.headers.get("Content-Disposition") ?? "";
    const filename = /filename="([^"]+)"/.exec(disposition)?.[1] ?? "historial-acm.csv";
    return { blob: await response.blob(), filename };
  }

  async importXlsx(file: File, analysisDate?: string): Promise<{ created: boolean; record: Acm; warnings: string[] }> {
    let response: Response;
    try {
      response = await fetch(`${apiBaseUrl}/v1/acms/import-xlsx`, {
        method: "POST",
        headers: {
          ...(this.token ? { Authorization: `Bearer ${await this.token()}` } : {}),
          "Content-Type": "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
          "X-File-Name": encodeURIComponent(file.name),
          ...(analysisDate ? { "X-Analysis-Date": analysisDate } : {}),
        },
        credentials: "include",
        body: file,
      });
    } catch (cause) { throw new Error(networkFailureMessage(cause)); }
    if (!response.ok) {
      const body = await response.json().catch(() => undefined) as ApiErrorBody | undefined;
      throw this.failure(response.status, body, "No se pudo importar el Excel. Comprobá su formato e intentá nuevamente.");
    }
    return response.json() as Promise<{ created: boolean; record: Acm; warnings: string[] }>;
  }

  async duplicateAcm(acmId: string): Promise<Acm> {
    return this.request<Acm>(`/v1/acms/${encodeURIComponent(acmId)}/duplicate`, { method: "POST" });
  }

  async uploadAcmFile(acmId: string, file: File, purpose: AcmFile["purpose"]): Promise<AcmFile> {
    let response: Response;
    try {
      response = await fetch(`${apiBaseUrl}/v1/acms/${encodeURIComponent(acmId)}/files`, {
        method: "POST",
        headers: {
          ...(this.token ? { Authorization: `Bearer ${await this.token()}` } : {}),
          "Content-Type": file.type,
          "X-File-Name": encodeURIComponent(file.name),
          "X-File-Purpose": purpose,
        },
        credentials: "include",
        body: file,
      });
    } catch (cause) { throw new Error(networkFailureMessage(cause)); }
    if (!response.ok) {
      const body = await response.json().catch(() => undefined) as ApiErrorBody | undefined;
      throw this.failure(response.status, body, "No se pudo subir el archivo. Intentá nuevamente.");
    }
    return response.json() as Promise<AcmFile>;
  }

  private failure(status: number, body: ApiErrorBody | undefined, fallback: string, options: RequestOptions = {}): ApiRequestError {
    if (status === 401 && options.unauthenticated !== "ignore") this.onUnauthenticated?.();
    return new ApiRequestError(status, apiFailureMessage(status, body, fallback));
  }
}

export function acmFileContentUrl(acmId: string, fileId: string): string {
  return `${apiBaseUrl}/v1/acms/${encodeURIComponent(acmId)}/files/${encodeURIComponent(fileId)}/content`;
}
